/**
 * Controls, framework coverage, and adversarial assurance.
 *
 * THE THING THIS SCREEN DOES NOT HAVE is a readiness percentage, and its absence is the design.
 * Every competing product renders "SOC 2: 87%". That number is produced by counting ticked boxes,
 * it averages controls of wildly different consequence, and it has to put unmeasurable controls
 * somewhere — which is never the failing side, because the demo has to look good.
 *
 * So coverage renders as three separate counts that are never summed, and the third one is given
 * the same visual weight as the other two. A reader who wants a percentage can divide, and in doing
 * so will have made the choice themselves about where the unmeasurable third goes.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { missingRequiredThreats, type ThreatId } from "@hub/core/browser";
import { api, relative, useApi } from "../lib/api";
import { CampaignGap, Card, Pill, Toast } from "../components";

interface Verdict { controlId: string; state: "in-force" | "failing" | "unknown"; detail: string; offenders?: { id: string; label: string }[] }
interface ControlRow {
  id: string; name: string; statement: string; assurance: "automatic" | "manual";
  probe?: string; whyManual?: string; layers: string[]; verdict: Verdict;
}
interface Coverage {
  framework: { id: string; name: string; scope: string; caveat: string };
  met: { control: string; refs: { ref: string; title: string }[]; detail: string }[];
  failing: { control: string; refs: { ref: string; title: string }[]; detail: string }[];
  unmeasurable: { control: string; refs: { ref: string; title: string }[]; why: string }[];
  clausesTouched: string[];
  disclaimer: string;
}
interface Campaign {
  id: string; name: string; status: string; outcome: string | null; ranBy: string;
  independent: boolean; startedAt: string; completedAt: string | null; reportRef: string | null;
  threatsCovered: string[];
  target: { id: string; displayName: string } | null;
  findings: { id: string; severity: string; status: string; title: string; controlGap: string }[];
}

type Tab = "controls" | "frameworks" | "adversarial";

export function Assurance() {
  const [tab, setTab] = useState<Tab>("controls");
  const [toast, setToast] = useState<string | null>(null);
  const controls = useApi<{ controls: ControlRow[]; counts: { inForce: number; failing: number; unknown: number } }>("/v1/assurance/controls");
  const frameworks = useApi<{ frameworks: Coverage[] }>("/v1/assurance/frameworks");
  const campaigns = useApi<{ campaigns: Campaign[]; requiredForTier3: ThreatId[] }>("/v1/adversarial/campaigns");

  async function snapshot() {
    try {
      const r = await api<{ assessedAt: string; counts: { failing: number } }>(
        "/v1/assurance/assess", { method: "POST", body: "{}" });
      setToast(`Snapshot recorded. ${r.counts.failing} control(s) failing at ${new Date(r.assessedAt).toISOString().slice(11, 19)} UTC.`);
    } catch (e) {
      setToast(e instanceof Error ? e.message : "That did not work.");
    }
    setTimeout(() => setToast(null), 4000);
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Assurance</h1>
          <p className="sub">
            What is actually in force, what it evidences against published frameworks, and whether
            anybody has tried to break it. Every control state below is a live query — there is no
            screen anywhere in this product that lets a person mark a control as satisfied.
          </p>
        </div>
        <button className="btn" onClick={snapshot}>Record a dated snapshot</button>
      </div>

      <div className="chips" style={{ marginBottom: 14 }}>
        {([["controls", "Controls"], ["frameworks", "Frameworks"], ["adversarial", "Adversarial"]] as const)
          .map(([k, label]) => (
            <button key={k} className={`chip ${tab === k ? "on" : ""}`} onClick={() => setTab(k)}
                    data-testid={`tab-${k}`}>
              {label}
            </button>
          ))}
      </div>

      {tab === "controls" && (
        <>
          {controls.loading && <p className="empty">Running the probes…</p>}
          {controls.data && (
            <>
              <div className="grid k3" style={{ marginBottom: 14 }}>
                <Count label="In force" n={controls.data.counts.inForce} tone="ok"
                       note="Measured by a query, this minute." />
                <Count label="Failing" n={controls.data.counts.failing} tone="bad"
                       note="Measured, and not working." />
                <Count label="Not measurable from here" n={controls.data.counts.unknown} tone="warn"
                       note="No probe this platform can honestly write. Never counted as passing." />
              </div>
              <div className="stack">
                {(["failing", "unknown", "in-force"] as const).map((state) => {
                  const rows = controls.data!.controls.filter((c) => c.verdict.state === state);
                  if (rows.length === 0) return null;
                  return (
                    <Card key={state} title={
                      state === "failing" ? `Failing (${rows.length})`
                        : state === "unknown" ? `Not measurable (${rows.length})`
                          : `In force (${rows.length})`
                    }>
                      <div className="stack" style={{ gap: 10 }}>
                        {rows.map((c) => (
                          <div key={c.id} style={{
                            paddingLeft: 12,
                            borderLeft: `3px solid var(--${state === "in-force" ? "ok" : state === "failing" ? "bad" : "warn"})`,
                          }}>
                            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                              <strong style={{ fontSize: 13.5 }}>{c.name}</strong>
                              <Pill tone={c.assurance === "automatic" ? "dim" : "warn"}>
                                {c.assurance === "automatic" ? "probed" : "manual"}
                              </Pill>
                            </div>
                            <div className="sub" style={{ fontSize: 12.5, marginTop: 2 }}>{c.statement}</div>
                            <div style={{ fontSize: 12.5, marginTop: 4, color: "var(--muted)" }}>
                              {c.verdict.detail}
                            </div>
                            {c.verdict.offenders && c.verdict.offenders.length > 0 && (
                              <div style={{ fontSize: 12, marginTop: 4, color: "var(--dim)" }}>
                                {c.verdict.offenders.slice(0, 6).map((o) => o.label).join(" · ")}
                                {c.verdict.offenders.length > 6 && ` … and ${c.verdict.offenders.length - 6} more`}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {tab === "frameworks" && (
        <div className="stack">
          {frameworks.loading && <p className="empty">Computing coverage…</p>}
          {frameworks.data?.frameworks.map((f) => (
            <Card key={f.framework.id} title={f.framework.name}>
              <p className="sub" style={{ marginTop: 0, fontSize: 12.5 }}>{f.framework.scope}</p>

              {/* THREE COUNTS, NEVER SUMMED, EQUALLY WEIGHTED. */}
              <div className="grid k3" style={{ marginTop: 12, marginBottom: 12 }}>
                <Count label="Measured in force" n={f.met.length} tone="ok" />
                <Count label="Measured failing" n={f.failing.length} tone="bad" />
                <Count label="Not measurable" n={f.unmeasurable.length} tone="warn" />
              </div>

              <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 10 }}>
                Touches {f.clausesTouched.length} clause(s): {f.clausesTouched.join(", ")}.
              </div>

              {f.failing.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 12, color: "var(--bad)", fontWeight: 600, marginBottom: 4 }}>
                    Failing, and mapped here
                  </div>
                  {f.failing.map((x) => (
                    <div key={x.control} style={{ fontSize: 12.5, color: "var(--muted)" }}>
                      {x.refs.map((r) => r.ref).join(", ")} — {x.detail}
                    </div>
                  ))}
                </div>
              )}

              {/* The caveat is the sentence that stops this page reading as a certification, so
                  it gets body-text contrast rather than the non-essential token. */}
              <p className="sub" style={{ fontSize: 12, marginBottom: 0 }}>
                {f.framework.caveat}
              </p>
            </Card>
          ))}
        </div>
      )}

      {tab === "adversarial" && (
        <div className="stack">
          {campaigns.loading && <p className="empty">Loading…</p>}
          {/* The required set comes from the API, not from a list kept in this file: the server is
              the authority on what a tier-3 promotion must have been tested against, and a copy
              here would be the second enumeration that drifts. Six since 2026-10-05. */}
          {campaigns.data && (
            <p className="sub" data-testid="required-for-tier3" style={{ fontSize: 12.5 }}>
              A tier-3 promotion relies on one passing, current, independent campaign that covered all
              of: {campaigns.data.requiredForTier3.join(", ")}. A campaign that missed any of them is
              marked below; it cannot carry a promotion on its own.
            </p>
          )}
          {campaigns.data && campaigns.data.campaigns.length === 0 && (
            <p className="empty">
              No adversarial campaign has been recorded. Every tier-3 agent needs one before promotion.
            </p>
          )}
          {campaigns.data?.campaigns.map((c) => {
            const blocking = c.findings.filter(
              (f) => (f.severity === "critical" || f.severity === "high") && f.status !== "remediated");
            // The same function the gate uses, over the same list the server sent — so what this
            // card says is missing is what the promotion endpoint would refuse for.
            const missing = missingRequiredThreats(c.threatsCovered, campaigns.data?.requiredForTier3 ?? []);
            return (
              <Card key={c.id}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <strong>{c.name}</strong>
                      <Pill tone={
                        c.status !== "complete" ? "dim"
                          : c.outcome === "passed" ? "ok"
                            : c.outcome === "inconclusive" ? "warn" : "bad"
                      }>
                        {c.status === "complete" ? c.outcome : c.status}
                      </Pill>
                      {/* Independence is a fact about the campaign, shown next to its outcome,
                          because a pass by the owning team is not the same evidence. */}
                      <Pill tone={c.independent ? "ok" : "warn"}>
                        {c.independent ? "independent" : "not independent"}
                      </Pill>
                    </div>
                    <div className="sub" style={{ fontSize: 12.5, marginTop: 4 }}>
                      {c.target ? <>against <Link to={`/agents/${c.target.id}`}>{c.target.displayName}</Link></>
                                : "estate-wide cycle"}
                      {" · "}run by {c.ranBy}
                      {c.completedAt ? <> · completed {relative(c.completedAt)}</> : <> · started {relative(c.startedAt)}</>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--dim)", marginTop: 4 }}>
                      covered: {c.threatsCovered.join(", ")}
                    </div>
                    <CampaignGap missing={missing}
                      note="a pass here cannot support a tier-3 promotion on its own" />
                  </div>
                  {c.reportRef && <a className="btn small" href={c.reportRef} rel="noreferrer">Write-up</a>}
                </div>

                {c.findings.length > 0 && (
                  <div style={{ marginTop: 12, borderTop: "1px solid var(--line-soft)", paddingTop: 10 }}>
                    {blocking.length > 0 && (
                      <div style={{ fontSize: 12, color: "var(--bad)", marginBottom: 6 }}>
                        {blocking.length} outstanding critical or high finding(s) — these block every
                        tier-3 promotion in scope, including one supported by a later passing campaign.
                      </div>
                    )}
                    <div className="stack" style={{ gap: 6 }}>
                      {c.findings.map((f) => (
                        <div key={f.id} style={{ fontSize: 12.5, display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                          <Pill tone={
                            f.status === "remediated" ? "ok"
                              : f.severity === "critical" || f.severity === "high" ? "bad" : "warn"
                          }>
                            {f.severity}
                          </Pill>
                          <span>{f.title}</span>
                          <span style={{ color: "var(--dim)" }}>
                            gap: {f.controlGap} · {f.status}
                            {f.status === "accepted" && " (accepted — still counts as outstanding)"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {toast && <Toast message={toast} />}
    </>
  );
}

function Count({ label, n, tone, note }: { label: string; n: number; tone: "ok" | "bad" | "warn"; note?: string }) {
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className="value" style={{ color: `var(--${tone})` }}>{n}</div>
      {note && <div className="delta">{note}</div>}
    </div>
  );
}
